/**
 * Consent-gated loader for optional third-party scripts.
 *
 * - Google Analytics (GA4) loads only after the user grants the "analytics"
 *   cookie category via the CookieConsent UI.
 * - Google AdSense loads when VITE_ADS_ENABLED === "true" and a publisher
 *   client is configured. It runs under Google Consent Mode v2 denied-by-default
 *   signals; personalisation is governed by the consent state, not by this loader.
 *
 * Reuses the existing preference store (`cookie-consent-v1` in localStorage)
 * from `src/components/CookieConsent.tsx` and listens for the
 * `cookie-prefs-changed` event it dispatches. If a user later revokes consent
 * we set the GA opt-out flag and prevent any further loads.
 */

import { getCookiePrefs, type CookiePrefs } from "@/components/CookieConsent";
import { isAdEligiblePath } from "@/lib/adRoutes";

const GA_ID = "G-LDT29MVLM5";
const GA_DISABLE_KEY = `ga-disable-${GA_ID}` as const;
const ADSENSE_CLIENT =
  (import.meta.env.VITE_ADSENSE_CLIENT as string | undefined) ??
  "ca-pub-3408228222825614";
const ADS_ENABLED = import.meta.env.VITE_ADS_ENABLED === "true";

let gaLoaded = false;
let adsLoaded = false;

type GtagFn = (...args: unknown[]) => void;

/**
 * Google Consent Mode v2.
 *
 * We install the `dataLayer`/`gtag` stub and push `consent: default` with every
 * advertising and analytics signal DENIED before any Google tag can load. The
 * existing cookie-preference store then drives `consent: update`. This makes the
 * denial explicit to Google tags before AdSense or GA4 are injected.
 */
function ensureGtag(): GtagFn {
  const w = window as unknown as { dataLayer?: unknown[]; gtag?: GtagFn };
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== "function") {
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    } as unknown as GtagFn;
  }
  return w.gtag!;
}

let consentDefaultsSet = false;

function setConsentDefaults() {
  if (consentDefaultsSet) return;
  consentDefaultsSet = true;
  // index.html already pushes the identical `consent default` from the
  // top-level document, before any Google tag. Never push it twice.
  if ((window as unknown as Record<string, unknown>).__tlConsentDefaultsSet) return;
  const gtag = ensureGtag();
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
}


function updateConsent(prefs: CookiePrefs | null) {
  const gtag = ensureGtag();
  gtag("consent", "update", {
    ad_storage: prefs?.marketing ? "granted" : "denied",
    ad_user_data: prefs?.marketing ? "granted" : "denied",
    ad_personalization: prefs?.marketing ? "granted" : "denied",
    analytics_storage: prefs?.analytics ? "granted" : "denied",
  });
}

function loadGA() {
  if (gaLoaded) return;
  gaLoaded = true;
  // Clear any prior opt-out.
  (window as unknown as Record<string, unknown>)[GA_DISABLE_KEY] = false;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  const gtag = ensureGtag();
  gtag("js", new Date());
  gtag("config", GA_ID, { anonymize_ip: true });
}

function disableGA() {
  // GA respects this window flag and will drop any subsequent hits even if the
  // script has already loaded. See https://developers.google.com/analytics/devguides/collection/gtagjs/user-opt-out
  (window as unknown as Record<string, unknown>)[GA_DISABLE_KEY] = true;
}


export function loadAdSense() {
  if (adsLoaded) return;
  if (!ADS_ENABLED || !ADSENSE_CLIENT) return;
  // Manual editorial units only: never load the tag outside the three
  // eligible article routes (see src/lib/adRoutes.ts).
  if (!isAdEligiblePath(window.location.pathname)) return;
  // The official tag is already in the top-level document (index.html).
  if (
    (window as unknown as Record<string, unknown>).__tlAdSenseTagLoaded ||
    document.querySelector('script[src*="adsbygoogle.js"]')
  ) {
    adsLoaded = true;
    return;
  }
  adsLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.crossOrigin = "anonymous";
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
  document.head.appendChild(s);
}


function applyPrefs(prefs: CookiePrefs | null) {
  updateConsent(prefs);

  if (prefs?.analytics) loadGA();
  else disableGA();

  // Load the AdSense tag under denied-by-default Consent Mode v2.
  // Personalisation is governed by the consent state, not by this loader.
  loadAdSense();
}

export function initConsentScripts() {
  if (typeof window === "undefined") return;
  // Consent Mode v2: deny everything (ads included) before any Google tag runs.
  setConsentDefaults();
  // Default to opt-out until we know the user's preference.
  disableGA();


  const run = () => applyPrefs(getCookiePrefs());
  if (document.readyState === "complete") run();
  else window.addEventListener("load", run, { once: true });

  window.addEventListener("cookie-prefs-changed", (e: Event) => {
    const detail = (e as CustomEvent<CookiePrefs>).detail ?? getCookiePrefs();
    applyPrefs(detail);
  });
}
